import { useState, useCallback, useRef, useEffect } from "react";
import useAxios from "../useAxios";
import { useApiResponseHandler } from "../useApiResponseHandler";
import {
  ApiResponse,
  AuditTrailEntry,
  AuditTrailPagedData,
} from "../../services/Dtos";
import { ApiService } from "../../services/ApiService";
import { useSchoolsContext } from "../../Contexts/schoolContext";

interface FilterParams {
  userId?: string;
  action?: string;
  entityName?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  pageNumber: number;
  pageSize: number;
}

export const useAuditTrailService = () => {
  const [auditTrails, setAuditTrails] = useState<AuditTrailEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize] = useState(20);

  const axiosClient = useAxios();
  const { handleApiError, handleApiSuccess, clearErrors } =
    useApiResponseHandler({
      setErrors,
    });
  const { users } = useSchoolsContext();

  const currentPageRef = useRef(currentPage);
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  const fetchAuditTrails = useCallback(
    async (
      filters: Omit<FilterParams, "pageNumber" | "pageSize"> = {},
      onSuccess?: (auditTrails: AuditTrailEntry[]) => void,
      pageNumber?: number
    ): Promise<{ result: ApiResponse }> => {
      setLoading(true);
      clearErrors();

      try {
        // Build query parameters for the filtered endpoint
        const params = new URLSearchParams();

        // Add filters
        if (filters.userId) params.append("userId", filters.userId);
        if (filters.action) params.append("action", filters.action);
        if (filters.entityName && filters.entityName !== "All entities")
          params.append("entityName", filters.entityName);
        if (filters.startDate) params.append("startDate", filters.startDate);
        if (filters.endDate) params.append("endDate", filters.endDate);
        if (filters.search) params.append("search", filters.search);

        // Add pagination parameters - use provided page number or current page
        const pageToUse = pageNumber || currentPageRef.current;
        params.append("pageNumber", pageToUse.toString());
        params.append("pageSize", pageSize.toString());

        const url = `audittrails/filtered?${params.toString()}`;

        // const response = await axiosClient.get<AuditTrailResponse>(url);
        const response = await new ApiService<AuditTrailPagedData>(
          url,
          axiosClient
        ).getAll();

        if (response.succeeded) {
          setAuditTrails(response.data.data);
          setTotalPages(response.data.totalPages);
          setCurrentPage(response.data.pageNumber);
          setTotalRecords(response.data.totalRecords);
       
          handleApiSuccess("Audit trails fetched successfully");
          onSuccess?.(response.data.data);
          return { result: response };
        } else {
          handleApiError(response.data, "Failed to fetch audit trails");
          return { result: response };
        }
      } catch (err: any) {
        const errorMessage = handleApiError(
          err,
          "Failed to fetch audit trails"
        );
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to fetch audit trails",
            statusCode: 500,
            errors: err.response?.data.errors || [],
          },
        };
      } finally {
        setLoading(false);
      }
    },
    [axiosClient, handleApiError, clearErrors, handleApiSuccess, pageSize]
  );

  const getUserInfo = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    return user
      ? { name: `${user.firstName} ${user.lastName}`, email: user.email }
      : { name: userId, email: userId };
  };

  const userOptions = users.map((user) => ({
    label: `${user.firstName} ${user.lastName} (${user.email})`,
    value: user.id,
  }));

  const getActionColor = (action: string) => {
    switch (action) {
      case "CREATE":
        return "green";
      case "UPDATE":
        return "blue";
      case "DELETE":
        return "red";
      case "RESULT_ENTRY":
        return "teal";
      default:
        return "gray";
    }
  };

  return {
    auditTrails,
    loading,
    errors,
    currentPage,
    totalPages,
    setTotalPages,
    totalRecords,
    setTotalRecords,
    pageSize,
    setAuditTrails,
    fetchAuditTrails,
    setCurrentPage,
    setLoading,
    clearErrors,
    getUserInfo,
    userOptions,
    getActionColor,
  };
};
